import { FC, createContext, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { CustomButton, CustomPopover, GenericError, Loader, Tabs } from 'components';
import { BackIcon, ChevronArrowRightIcon, DropDownChevronDownIcon, SendReportIcon, WarningIcon } from 'assets/icons';
import {
  useApproveMemberPayRunMutation,
  useGetPayRunMemberDetailsQuery,
  useSendPayRunReportMutation
} from 'containers/accounting/pay-runs/api';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { ButtonType } from 'components/custom-button/CustomButton';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamAccountingPermissions,
  showNotifier
} from 'containers/app/appSlice';
import MemberApproveButton from './components/MemberApproveButton';
import MemberReviewSummaryCard from './components/MemberReviewSummaryCard';
import MemberAdjustmentsTab from './components/MemberAdjustmentsTab';
import { JobReviewTabStatus, MemberReviewTabs, PayRunMemberApprovalType } from './constants';
import ConfirmJobsTab from './confirm-jobs/ConfirmJobsTab';
import { initializeStatusCounts, setSelectedReviewTab } from './confirm-jobs/confirmJobsSlice';
import {
  navigateToNextMember,
  navigateToPreviousMember,
  selectHasNextMember,
  selectHasPreviousMember,
  selectMembersList,
  setCurrentMemberIndex
} from './memberNavigationSlice';
import MemberHistoryTab from './member-history-tab/MemberHistoryTab';
import { Tab } from 'components/tabs/Tabs';
import { getPayRunPeriodInEST } from './utils';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from './events';
import { NotifierTypes } from 'constants/notifierConstants';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';

// Create a context for job navigation
export const JobNavigationContext = createContext({
  getNextJob: (_currentJobId: string) => ({ jobId: '', status: '' }),
  getPreviousJob: (_currentJobId: string) => ({ jobId: '', status: '' })
});

export const PayRunContext = createContext<{
  payRunStatus?: string;
  memberDetails?: {
    id: string;
    profilePictureURL: string;
    name: string;
    email: string;
    phone: string;
  };
}>({
  payRunStatus: undefined,
  memberDetails: undefined
});

const MemberReview: FC = () => {
  const navigate = useNavigate();
  const { payRunId, merchantId } = useParams<{
    payRunId: string;
    merchantId: string;
  }>();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { isActiveFSMPlusPlan } = useFSMPlanInfo();

  const [selectedTab, setSelectedTab] = useState<string>(MemberReviewTabs.CONFIRM_JOBS);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [showMembersDropdown, setShowMembersDropdown] = useState<boolean>(false);

  // Member navigation selectors
  const membersList = useAppSelector(selectMembersList);
  const hasNextMember = useAppSelector(selectHasNextMember);
  const hasPreviousMember = useAppSelector(selectHasPreviousMember);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { managePayAdjustments } = currentTeamAccountingPermissions ?? {};

  const {
    data: memberDetails,
    isLoading,
    isFetching,
    isError: isMemberDetailsError,
    refetch: refetchMemberDetails
  } = useGetPayRunMemberDetailsQuery({
    teamId: currentTeam.id,
    payRunId,
    merchantId
  });

  // Get member details once data is available
  const {
    merchantDetails,
    payRunDetails,
    jobDetails,
    totalAmount,
    grossPayAmount,
    jobAdjustmentAmount,
    otherAdjustmentAmount,
    totalAdjustmentAmount
  } = memberDetails ?? {};

  const [approveMemberPayRun] = useApproveMemberPayRunMutation();
  const [sendPayRunReport, { isLoading: isSendingReport }] = useSendPayRunReportMutation();
  const notifications = useAppSelector(getNotifications);

  // Create dummy navigation functions that will be overridden by ConfirmJobsTab
  const [jobNavigationFunctions, setJobNavigationFunctions] = useState({
    getNextJob: (_currentJobId: string) => ({ jobId: '', status: '' }),
    getPreviousJob: (_currentJobId: string) => ({ jobId: '', status: '' })
  });

  useEffect(() => {
    dispatch(setSelectedReviewTab(JobReviewTabStatus.NEEDS_REVIEW));
  }, []);

  useEffect(() => {
    if (memberDetails?.jobDetails) {
      dispatch(
        initializeStatusCounts({
          needsReviewJobs: memberDetails.jobDetails.needsReviewJobs,
          inProgressJobs: memberDetails.jobDetails.inProgressJobs,
          confirmedJobs: memberDetails.jobDetails.confirmedJobs,
          skippedJobs: memberDetails.jobDetails.skippedJobs,
          totalJobs: memberDetails.jobDetails.totalJobs
        })
      );
    }
  }, [memberDetails, dispatch]);

  useEffect(() => {
    // Find and set the current member index when component mounts or merchantId changes
    if (membersList.length > 0 && merchantId) {
      const index = membersList.findIndex(member => member.memberId === merchantId);
      if (index !== -1) {
        dispatch(setCurrentMemberIndex(index));
      }
    }
  }, [membersList, merchantId, dispatch]);

  // Reset isNavigating when data is loaded after navigation
  useEffect(() => {
    if (memberDetails && isNavigating && !isFetching) {
      setIsNavigating(false);
    }
  }, [memberDetails, isNavigating, isFetching]);

  useEffect(() => {
    setSelectedTab(MemberReviewTabs.CONFIRM_JOBS);
    dispatch(setSelectedReviewTab(JobReviewTabStatus.NEEDS_REVIEW));
  }, [isNavigating]);

  useEffect(() => {
    if (payRunId && merchantId) {
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunMemberReviewView, {
        payRunId,
        merchantId
      });
    }
  }, [payRunId, merchantId]);

  const tabsList: Tab[] = [
    { id: 1, title: MemberReviewTabs.CONFIRM_JOBS },
    {
      id: 2,
      title: MemberReviewTabs.ADJUSTMENTS,
      icon:
        memberDetails?.suggestedOtherAdjustmentCount > 0 && managePayAdjustments ? (
          <WarningIcon className="path-stroke-current text-pending-orange" />
        ) : null,
      iconPosition: 'right'
    },
    { id: 3, title: MemberReviewTabs.HISTORY }
  ];

  const handlePayRunApproval = (type: PayRunMemberApprovalType) => {
    approveMemberPayRun({
      teamId: currentTeam.id,
      payRunId,
      payload: {
        merchantId,
        type
      }
    }).then(() => {
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunMemberReviewApproval, {
        payRunId,
        merchantId,
        approvalType: type
      });
    });
  };

  const handleSendReport = () => {
    if (!merchantDetails?.email) {
      const existingNotification = notifications.find(notification => notification.id === 'send-report');
      if (existingNotification) return;
      dispatch(
        showNotifier({
          id: 'send-report',
          message: {
            primaryMessage: 'Reports not sent due to missing email address'
          },
          type: NotifierTypes.WARNING,
          showClose: true
        })
      );
    }
    if (payRunId && merchantId && merchantDetails?.email) {
      sendPayRunReport({
        teamId: currentTeam.id,
        payRunId,
        merchantIds: [merchantId]
      }).then(() => {
        logAnalyticEvent(PayRunsCleverTapEvents.webPayRunMemberReportSent, {
          payRunId,
          merchantId
        });
      });
    }
  };

  const handleNavigateToPrevMember = () => {
    if (hasPreviousMember) {
      setIsNavigating(true);
      dispatch(navigateToPreviousMember());
      const prevIndex = membersList.findIndex(m => m.memberId === merchantId) - 1;
      if (prevIndex >= 0) {
        const prevMemberId = membersList[prevIndex].memberId;
        navigate(`/accounting/pay-runs/${payRunId}/members/${prevMemberId}`);
      }
    }
  };

  const handleNavigateToNextMember = () => {
    if (hasNextMember) {
      setIsNavigating(true);
      dispatch(navigateToNextMember());
      const nextIndex = membersList.findIndex(m => m.memberId === merchantId) + 1;
      if (nextIndex < membersList.length) {
        const nextMemberId = membersList[nextIndex].memberId;
        navigate(`/accounting/pay-runs/${payRunId}/members/${nextMemberId}`);
      }
    }
  };

  const handleSidePanelClose = () => {
    navigate(`/accounting/pay-runs/${payRunId}/members/${merchantId}`, { replace: true });
  };

  // Handle initial loading
  if (isLoading) {
    return <Loader />;
  }

  if (isMemberDetailsError) {
    return <GenericError handleErrorBack={refetchMemberDetails} />;
  }

  const handleBackButtonClick = () => {
    navigate(`/accounting/pay-runs/pay-period-review/${currentTeam.id}/${payRunId}`);
  };

  return (
    <>
      <div className="flex h-full flex-col">
        {/* Header Section */}
        <div
          id="member-review-header"
          className="flex items-center justify-between border-b border-borderGray px-6 py-2">
          <div className="flex w-[40%] items-center">
            <button
              onClick={() => handleBackButtonClick()}
              className="flex items-center gap-2 font-bold text-primaryText"
              disabled={isLoading}>
              <BackIcon className="path path-stroke-current h-4 w-4" />
              <span className="whitespace-nowrap text-xl">Member review</span>
            </button>
            <div className="mx-[6px] inline-block h-1 w-1 min-w-[4px] rounded-full bg-accentText"></div>
            <div className="flex flex-wrap text-sm font-semibold text-accentText xl:flex-nowrap">
              {payRunDetails.payRunNumber}{' '}
              <span className="whitespace-nowrap">
                (
                {getPayRunPeriodInEST({
                  payRunFrom: payRunDetails.payPeriodFrom,
                  payRunTo: payRunDetails.payPeriodTo
                })}
                )
              </span>
            </div>
          </div>

          <div
            className={`flex w-[20%] items-center justify-center gap-2 ${
              isNavigating ? 'pointer-events-none opacity-50' : ''
            }`}>
            <button
              onClick={handleNavigateToPrevMember}
              disabled={!hasPreviousMember || isLoading}
              className={`rounded-full ${
                hasPreviousMember && !isLoading
                  ? 'bg-highlightGray p-2 text-secondary hover:bg-highlightGray/80'
                  : 'opacity-0'
              }`}>
              <ChevronArrowRightIcon className="h-4 w-4 rotate-180" />
            </button>

            {membersList.length > 0 ? (
              <CustomPopover
                anchorComponent={({ isPanelOpen }) => (
                  <button className="mx-4 flex items-center gap-1">
                    <span className="text-17px font-bold text-primaryText">{merchantDetails.name}</span>
                    <DropDownChevronDownIcon
                      className={`ml-2 h-2 w-2 shrink-0 transition-transform
                       ${isPanelOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                )}
                show={showMembersDropdown}
                onTogglePopover={() => setShowMembersDropdown(!showMembersDropdown)}
                onClosePopover={() => setShowMembersDropdown(false)}
                offset="right-0 left-0"
                margin="mt-2"
                width="w-full">
                <div className="max-h-[400px] divide-y overflow-y-auto">
                  {membersList.map(member => (
                    <button
                      key={member.memberId}
                      onClick={() => {
                        setShowMembersDropdown(false);
                        setIsNavigating(true);
                        navigate(`/accounting/pay-runs/${payRunId}/members/${member.memberId}`);
                      }}
                      className={`w-full py-2 text-left text-sbase text-primaryText hover:bg-highlightGray ${
                        member.memberId === merchantId ? 'font-bold' : 'font-semibold'
                      }`}>
                      {member.name}
                    </button>
                  ))}
                </div>
              </CustomPopover>
            ) : (
              <span className="mx-4 text-17px font-bold text-primaryText">{merchantDetails.name}</span>
            )}

            <button
              onClick={handleNavigateToNextMember}
              disabled={!hasNextMember || isLoading}
              className={`rounded-full ${
                hasNextMember && !isLoading
                  ? 'bg-highlightGray p-2 text-secondary hover:bg-highlightGray/80'
                  : 'opacity-0'
              }`}>
              <ChevronArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="flex h-10 w-[40%] items-center justify-end gap-4">
            <CustomButton
              isLoading={isSendingReport}
              id="send-report"
              type={ButtonType.SECONDARY}
              onClick={handleSendReport}
              className="border-none text-secondary"
              disabled={isLoading || isNavigating}>
              <div className="flex items-center gap-2 text-17px">
                <SendReportIcon />
                Send report
              </div>
            </CustomButton>

            <MemberApproveButton
              amount={getAmountWithCurrency(totalAmount)}
              status={payRunDetails.status}
              isReviewComplete={jobDetails.needsReviewJobs === 0 && jobDetails.inProgressJobs === 0}
              onApprovalAction={handlePayRunApproval}
              disabled={isNavigating || +memberDetails.suggestedOtherAdjustmentCount > 0 || !isActiveFSMPlusPlan}
            />
          </div>
        </div>

        {isNavigating && <Loader />}

        {!isLoading && !isNavigating && (
          <PayRunContext.Provider
            value={{
              payRunStatus: payRunDetails?.payRunStatus,
              memberDetails: merchantDetails
            }}>
            <>
              {/* Summary Stats */}
              <MemberReviewSummaryCard
                jobDetails={jobDetails}
                totalAmount={totalAmount}
                grossPayAmount={grossPayAmount}
                jobAdjustmentAmount={jobAdjustmentAmount}
                otherAdjustmentAmount={otherAdjustmentAmount}
                merchantDetails={merchantDetails}
                totalAdjustmentAmount={totalAdjustmentAmount}
                status={payRunDetails.status}
              />
              <div id="member-review-tabs" className="mt-4 border-b border-borderGray">
                <Tabs
                  tabs={tabsList}
                  selectedTab={selectedTab}
                  onTabSelect={setSelectedTab}
                  className="flex justify-center px-6"
                  tabStyle="px-4 pb-4"
                />
              </div>

              {/* Tab Content Section */}
              <div className="grow py-6">
                <JobNavigationContext.Provider value={jobNavigationFunctions}>
                  {selectedTab === MemberReviewTabs.CONFIRM_JOBS && (
                    <ConfirmJobsTab setJobNavigationFunctions={setJobNavigationFunctions} />
                  )}
                </JobNavigationContext.Provider>
                {selectedTab === MemberReviewTabs.HISTORY && <MemberHistoryTab />}
                {selectedTab === MemberReviewTabs.ADJUSTMENTS && (
                  <MemberAdjustmentsTab
                    adjustments={memberDetails.adjustments}
                    totalAdjustmentAmount={memberDetails.totalAdjustmentAmount}
                    suggestedAdjustmentCount={memberDetails.suggestedOtherAdjustmentCount}
                  />
                )}
              </div>
            </>
            <Outlet
              context={{
                handleSidePanelClose,
                jobNavigationFunctions
              }}
            />
          </PayRunContext.Provider>
        )}
      </div>
    </>
  );
};

export default MemberReview;
